import { type MousePosT } from '../../../providers/stage-provider';
import type { RectDataT } from '../../../types';
import { type EllipseAnnotationT, type EllipseDataT } from '../../../types';
import { type VertexT, VERTEX_TYPE } from '../shape/vertex';
import { BOX_BUFFER } from '../utils';
export const convertDeltaToMousePosition = ({
  centerX,
  centerY,
  radiusX,
  radiusY,
  deltaWidth,
  deltaHeight,
  vertexType
}): [number, number] => {
  if (vertexType === 'RIGHT' || vertexType === 'BOTTOM' || vertexType === 'BOTTOM_RIGHT') {
    return [centerX + radiusX + deltaWidth, centerY + radiusY + deltaHeight];
  }
  if (vertexType === 'LEFT' || vertexType === 'TOP' || vertexType === 'TOP_LEFT') {
    return [centerX - radiusX - deltaWidth, centerY - radiusY - deltaHeight];
  }
  if (vertexType === 'TOP_RIGHT') {
    return [centerX + radiusX + deltaWidth, centerY - radiusY - deltaHeight];
  }
  if (vertexType === 'BOTTOM_LEFT') {
    return [centerX - radiusX - deltaWidth, centerY + radiusY + deltaHeight];
  }
  return [centerX, centerY];
};
export const getEllipseVertices = (data: EllipseDataT): VertexT[] => {
  const {
    centerX,
    centerY,
    radiusX,
    radiusY
  } = data;
  return [{
    point: [centerX, centerY - radiusY],
    type: VERTEX_TYPE.TOP
  }, {
    point: [centerX + radiusX, centerY],
    type: VERTEX_TYPE.RIGHT
  }, {
    point: [centerX, centerY + radiusY],
    type: VERTEX_TYPE.BOTTOM
  }, {
    point: [centerX - radiusX, centerY],
    type: VERTEX_TYPE.LEFT
  }];
};
export const getDrawingEllipseCoords = (x0: number, y0: number, x1: number, y1: number, stageWidth: number, stageHeight: number) => {
  // Clamp coordinates to stage bounds if provided
  let clampedX0 = x0;
  let clampedY0 = y0;
  let clampedX1 = x1;
  let clampedY1 = y1;
  if (stageWidth !== undefined && stageHeight !== undefined) {
    clampedX0 = Math.max(0, Math.min(x0, stageWidth));
    clampedY0 = Math.max(0, Math.min(y0, stageHeight));
    clampedX1 = Math.max(0, Math.min(x1, stageWidth));
    clampedY1 = Math.max(0, Math.min(y1, stageHeight));
  }
  const radiusX = Math.abs(clampedX1 - clampedX0) / 2;
  const radiusY = Math.abs(clampedY1 - clampedY0) / 2;
  const centerX = Math.min(clampedX0, clampedX1) + radiusX;
  const centerY = Math.min(clampedY0, clampedY1) + radiusY;
  return {
    centerX: centerX,
    centerY: centerY,
    radiusX: radiusX,
    radiusY: radiusY
  };
};
export const getTranslatedEllipse = (deltaX: number, deltaY: number, ellipse: EllipseAnnotationT, stageWidth: number, stageHeight: number): EllipseAnnotationT => {
  let newCenterX = ellipse.data.centerX + deltaX;
  let newCenterY = ellipse.data.centerY + deltaY;
  if (stageWidth !== undefined && stageHeight !== undefined) {
    const minX = ellipse.data.radiusX;
    const maxX = stageWidth - ellipse.data.radiusX;
    const minY = ellipse.data.radiusY;
    const maxY = stageHeight - ellipse.data.radiusY;
    newCenterX = Math.max(minX, Math.min(newCenterX, maxX));
    newCenterY = Math.max(minY, Math.min(newCenterY, maxY));
  }
  return {
    ...ellipse,
    data: {
      ...ellipse.data,
      centerX: newCenterX,
      centerY: newCenterY
    }
  };
};
export const getTransformedPointsOnVertexMove = (ellipse: EllipseAnnotationT, mousePos: MousePosT, selectedVertexType: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE], multiSelectBox: RectDataT | null | undefined = null, stageWidth?: number, stageHeight?: number): EllipseAnnotationT => {
  let {
    centerX,
    centerY,
    radiusX,
    radiusY
  } = ellipse.data;

  // Clamp mouse position to stage bounds if provided
  let clampedMouseX = mousePos[0];
  let clampedMouseY = mousePos[1];
  if (stageWidth !== undefined && stageHeight !== undefined) {
    clampedMouseX = Math.max(0, Math.min(mousePos[0], stageWidth));
    clampedMouseY = Math.max(0, Math.min(mousePos[1], stageHeight));
  }
  const checkBoundsFromLeftChange = () => {
    if (!multiSelectBox) return;
    const outOfBound = centerX - radiusX - BOX_BUFFER / 2 < multiSelectBox.x;
    if (outOfBound) {
      if (multiSelectBox.x + multiSelectBox.width - BOX_BUFFER > centerX + radiusX * 2) {
        centerX = Math.max(multiSelectBox.x + BOX_BUFFER / 2, centerX);
        radiusX = Math.max(centerX - BOX_BUFFER / 2 - multiSelectBox.x, 1);
      } else {
        centerX = multiSelectBox.x + BOX_BUFFER / 2;
        radiusX = 1;
      }
    } else {
      const rightGreaterThanBoxRight = centerX + radiusX + BOX_BUFFER / 2 > multiSelectBox.x + multiSelectBox.width + 1;
      if (rightGreaterThanBoxRight) {
        centerX = multiSelectBox.x + multiSelectBox.width - BOX_BUFFER / 2;
        radiusX = 1;
      }
    }
  };
  const checkBoundsFromRightChange = () => {
    if (!multiSelectBox) return;
    const outOfBound = centerX + radiusX + BOX_BUFFER / 2 > multiSelectBox.x + multiSelectBox.width + 1;
    if (outOfBound) {
      if (multiSelectBox.x + multiSelectBox.width - BOX_BUFFER > centerX + radiusX * 2) {
        centerX = Math.min(multiSelectBox.x + multiSelectBox.width - BOX_BUFFER / 2, centerX);
        radiusX = Math.max(multiSelectBox.x + multiSelectBox.width - centerX - BOX_BUFFER / 2, 1);
      } else {
        centerX = multiSelectBox.x + multiSelectBox.width - BOX_BUFFER / 2;
        radiusX = 1;
      }
    } else {
      const leftSmallerThanBoxLeft = centerX - radiusX - BOX_BUFFER / 2 < multiSelectBox.x;
      if (leftSmallerThanBoxLeft) {
        centerX = multiSelectBox.x + BOX_BUFFER / 2;
        radiusX = 1;
      }
    }
  };
  const checkBoundsFromBottomChange = () => {
    if (!multiSelectBox) return;
    const outOfBound = centerY + radiusY + BOX_BUFFER / 2 + 1 > multiSelectBox.y + multiSelectBox.height;
    const bottomGreaterThanBoxTop = centerY + radiusY - BOX_BUFFER / 2 - 1 <= multiSelectBox.y;
    const ellipseCenterAboveTop = centerY - BOX_BUFFER / 2 <= multiSelectBox.y;
    if (outOfBound) {
      if (ellipseCenterAboveTop || bottomGreaterThanBoxTop) {
        centerY = multiSelectBox.y + multiSelectBox.height - BOX_BUFFER / 2;
        radiusY = 1;
      } else if (multiSelectBox.y + multiSelectBox.height > centerY + radiusY - BOX_BUFFER) {
        centerY = Math.min(multiSelectBox.y + multiSelectBox.height - BOX_BUFFER / 2, centerY);
        radiusY = Math.max(multiSelectBox.y + multiSelectBox.height - centerY - BOX_BUFFER / 2, 1);
      } else {
        centerY = multiSelectBox.y + multiSelectBox.height - BOX_BUFFER / 2;
        radiusY = 1;
      }
    } else {
      if (bottomGreaterThanBoxTop || ellipseCenterAboveTop) {
        centerY = multiSelectBox.y + BOX_BUFFER / 2;
        radiusY = 1;
      }
    }
  };
  const checkBoundsFromTopChange = () => {
    if (!multiSelectBox) return;
    const outOfBound = centerY - radiusY - BOX_BUFFER / 2 - 1 <= multiSelectBox.y;
    const topLowerThanBoxBottom = centerY - radiusY + BOX_BUFFER / 2 + 1 <= multiSelectBox.y;
    const ellipseCenterBelowTop = centerY + BOX_BUFFER / 2 >= multiSelectBox.y + multiSelectBox.height;
    if (outOfBound) {
      if (ellipseCenterBelowTop || topLowerThanBoxBottom) {
        centerY = multiSelectBox.y + BOX_BUFFER / 2;
        radiusY = 1;
      } else if (multiSelectBox.y + multiSelectBox.height > centerY + radiusY - BOX_BUFFER) {
        centerY = Math.max(multiSelectBox.y + BOX_BUFFER / 2, centerY);
        radiusY = Math.max(centerY - multiSelectBox.y - BOX_BUFFER / 2, 1);
      } else {
        centerY = multiSelectBox.y + BOX_BUFFER / 2;
        radiusY = 1;
      }
    } else {
      if (topLowerThanBoxBottom || ellipseCenterBelowTop) {
        centerY = multiSelectBox.y + multiSelectBox.height - BOX_BUFFER / 2;
        radiusY = 1;
      }
    }
  };
  const adjustTop = () => {
    const topY = clampedMouseY;
    const bottomY = centerY + radiusY;
    const newRadiusY = (bottomY - topY) / 2;
    centerY = topY + newRadiusY;
    radiusY = Math.abs(newRadiusY);
    checkBoundsFromTopChange();
  };
  const adjustRight = () => {
    const rightX = clampedMouseX;
    const leftX = centerX - radiusX;
    const newRadiusX = (rightX - leftX) / 2;
    centerX = leftX + newRadiusX;
    radiusX = Math.abs(newRadiusX);
    checkBoundsFromRightChange();
  };
  const adjustBottom = () => {
    const bottomY = clampedMouseY;
    const topY = centerY - radiusY;
    const newRadiusY = (bottomY - topY) / 2;
    centerY = topY + newRadiusY;
    radiusY = Math.abs(newRadiusY);
    checkBoundsFromBottomChange();
  };
  const adjustLeft = () => {
    const leftX = clampedMouseX;
    const rightX = centerX + radiusX;
    const newRadiusX = (rightX - leftX) / 2;
    centerX = leftX + newRadiusX;
    radiusX = Math.abs(newRadiusX);
    checkBoundsFromLeftChange();
  };
  switch (selectedVertexType) {
    case VERTEX_TYPE.TOP:
      {
        adjustTop();
        break;
      }
    case VERTEX_TYPE.RIGHT:
      {
        adjustRight();
        break;
      }
    case VERTEX_TYPE.BOTTOM:
      {
        adjustBottom();
        break;
      }
    case VERTEX_TYPE.LEFT:
      {
        adjustLeft();
        break;
      }
    case VERTEX_TYPE.TOP_RIGHT:
      {
        adjustTop();
        adjustRight();
        break;
      }
    case VERTEX_TYPE.BOTTOM_RIGHT:
      {
        adjustBottom();
        adjustRight();
        break;
      }
    case VERTEX_TYPE.TOP_LEFT:
      {
        adjustTop();
        adjustLeft();
        break;
      }
    case VERTEX_TYPE.BOTTOM_LEFT:
      {
        adjustBottom();
        adjustLeft();
        break;
      }
    default:
      break;
  }
  return {
    ...ellipse,
    data: {
      centerX,
      centerY,
      radiusX,
      radiusY
    }
  };
};
export const interpolateEllipse = (initialEllipse: EllipseDataT, finalEllipse: EllipseDataT, interpolationFactor: number): EllipseDataT => {
  return {
    centerX: initialEllipse.centerX + (finalEllipse.centerX - initialEllipse.centerX) * interpolationFactor,
    centerY: initialEllipse.centerY + (finalEllipse.centerY - initialEllipse.centerY) * interpolationFactor,
    radiusX: initialEllipse.radiusX + (finalEllipse.radiusX - initialEllipse.radiusX) * interpolationFactor,
    radiusY: initialEllipse.radiusY + (finalEllipse.radiusY - initialEllipse.radiusY) * interpolationFactor
  };
};